import { useEffect, useMemo, useState } from 'react'

import DropdownButton from '../../components/atoms/dropdown/Dropdown'
import InputCustom from '../../components/atoms/input/InputCustom'
import ButtonCustom from '../../components/atoms/button/ButtonCustom'
import LeaveConfirmationModal from '../../components/atoms/modal/LeaveConfirmationModal'
import CommonDeleteModal from '../../components/atoms/modal/CommonDeleteModal'
import { IndividualModal } from './IndividualModal'
import { GroupModal } from './GroupModal'
import ChatMembersModal from './ChatMembersModal'
import { GeneralPaymentPopup } from '../../components/popups/GeneralPaymentPopup'

import styles from './ChatPageV2.module.scss'
import { useDispatch, useSelector } from 'react-redux'

import {
	blockUser,
	deleteConversation,
	getDirectChatUsers,
	setCurrentSelectedUser,
	unblockUser,
	updateChatListMessage,
	updateUnreadCount,
	muteUnmuteUserNotification,
} from '../../features/messageSlice'
import {
	getUserGroups,
	leaveGroup,
	setCurrentSelectedGroup,
	updateGroupListMessage,
	updateGroupUnreadCount,
	addNewGroup,
	muteUnmuteGroupNotification
} from '../../features/groupSlice'
import { DirectChatList } from './DirectChatList'
import { GroupChatList } from './GroupChatList'
import { DirectChatWindow } from './DirectChatWindow'
import { GroupChatWindow } from './GroupChatWindow'
import { GroupInfo } from './GroupInfo'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { addFriendChat, getFriendsInfo } from '../../features/friendSlice'
import { useRef } from 'react'
import { isFreeTierVendorOrPlanner } from '../../utils/chatFreeTier'

const statusMap = {
	true: styles.online,
	false: styles.offline,
	away: styles.away,
}

export const ChatPage = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const location = useLocation()
	const { kazooId } = useParams()
	const [searchParams] = useSearchParams()
	const urlGroupId = searchParams.get('groupId')
	const targetGroupId = location.state?.selectedGroupId || urlGroupId
	const targetUserId = location.state?.selectedUserId
	const targetSelectedUserId = location.state?.selectedUser // legacy fallback

	const currentSelectedUser = useSelector(
		(s) => s.message.currentSelectedUser
	)

	const currentSelectedGroup = useSelector(
		(s) => s.group.currentSelectedGroup
	)

	const [isSidebarOpen, setIsSidebarOpen] = useState(false)
	const [newChatType, setNewChatType] = useState('')
	const [chatType, setChatType] = useState((targetGroupId || currentSelectedGroup) ? 'group' : 'user')
	const [chatOption, setChatOption] = useState('')
	const [chatMembers, setChatMembers] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [showMuteModal, setShowMuteModal] = useState(false)
	const [hasNewDirectMessages, setHasNewDirectMessages] = useState(false)
	const [hasNewGroupMessages, setHasNewGroupMessages] = useState(false)
	const [showUpgradePopup, setShowUpgradePopup] = useState(false)

	const handleSearchQueryChange = (e) => {
		setSearchQuery(e.target.value)
	}

	// -----------
	const me = useSelector((s) => s.auth.user)
	const accountType = useSelector((state) => state.auth.accountType)
	const payment = useSelector((state) => state.auth.user?.payment)
	const isFreeTierVendorOrPlannerUser = isFreeTierVendorOrPlanner(accountType, payment)

	// -----------
	const handleSetChatType = (type) => {
		setChatType(type)
		dispatch(setCurrentSelectedUser(null))
		dispatch(setCurrentSelectedGroup(null))
		setChatOption('')
		setSearchQuery('')

		// Clear indicator when switching to that tab
		if (type === 'user') {
			setHasNewDirectMessages(false)
		} else if (type === 'group') {
			setHasNewGroupMessages(false)
		}
	}

	const handleSelect = (option) => {
		setChatOption('')
		// If free-tier vendor/planner tries to create a group, show upgrade popup
		if (option.value === 'group' && isFreeTierVendorOrPlannerUser) {
			setShowUpgradePopup(true)
			return
		}
		setNewChatType(option.value)
	}

	const toggleSidebar = () => {
		setIsSidebarOpen(!isSidebarOpen)
	}

	const ChatMembersModalOpen = () => {
		setChatMembers(!chatMembers)
	}

	const ChatMembersModalClose = () => {
		setChatMembers(!chatMembers)
	}

	const closeSidebar = () => {
		setIsSidebarOpen(false)
	}

	const handleCloseModal = () => {
		setNewChatType('')
	}

	const handleSuccessIndividualModal = async (user) => {
		// await dispatch(addFriendChat(user._id)).unwrap()
		handleCloseModal()
		await dispatch(
			getDirectChatUsers({
				chatType: kazooId ? 'inSide' : 'outSide',
				kazooId: kazooId || '',
			})
		).unwrap()
		dispatch(
			setCurrentSelectedUser({
				...user,
				profilePic: user.avatar || user.profilePic,
			})
		)
	}

	const handleOptionSelect = (option) => {
		if (option.value === 'view_profile')
			navigate(`/profile/${currentSelectedUser._id}`)
		if (option.value === 'block') {
			if (currentSelectedUser.blocked) {
				dispatch(unblockUser(currentSelectedUser._id))
			} else {
				dispatch(blockUser(currentSelectedUser._id))
			}
		}
		if (option.value === 'delete_conversation') {
			setShowDeleteModal(true)
		}
		if (option.value === 'mute') {
			setShowMuteModal(true)
		}
		setChatOption(option.value)
	}

	const handleCloseDeleteModal = () => {
		setShowDeleteModal(false)
	}

	const handleCloseMuteModal = () => {
		setShowMuteModal(false)
	}

	const handleConfirmMute = () => {
		if (chatType === 'user' && currentSelectedUser) {
			dispatch(
				muteUnmuteUserNotification({
					userId: currentSelectedUser._id,
					is_mute_chat_notification: !currentSelectedUser.isMuted,
				})
			)
		} else if (chatType === 'group' && currentSelectedGroup) {
			dispatch(
				muteUnmuteGroupNotification({
					groupId: currentSelectedGroup._id,
					is_mute_chat_notification: !currentSelectedGroup.isMuted,
				})
			)
		}
		setShowMuteModal(false)
	}

	const handleConfirmDelete = async () => {
		if (chatType === 'user' && currentSelectedUser) {
			// Delete direct conversation
			await dispatch(deleteConversation({
				otherMemberId: currentSelectedUser._id,
				kazooId: kazooId || ''
			}))
		} else if (chatType === 'group' && currentSelectedGroup) {
			// Delete group conversation
			await dispatch(deleteConversation({
				groupId: currentSelectedGroup._id,
				kazooId: kazooId || ''
			}))
		}
		setShowDeleteModal(false)
	}

	const handleCloseLeave = () => {
		setChatOption('')
	}

	const handleLeave = () => {
		dispatch(leaveGroup(currentSelectedGroup._id))
		handleCloseLeave()
	}

	const chatDropdown = useMemo(
		() => [
			...(accountType === 'user' ||
				accountType === 'kazoo-planner' ||
				accountType === 'wedding-planner' ||
				accountType === 'vendor' ||
				accountType === 'vendor-staff' ||
				accountType === 'planner-staff'
				? [
					{
						label: 'Direct Chat',
						value: 'individual',
						showDivider: true,
						imgBefore: true,
						imgSrc: '/images/user.svg',
					},
				]
				: []),
			{
				label: 'Group Chat',
				value: 'group',
				imgBefore: true,
				imgSrc: '/images/people.svg',
			},
		],
		[accountType]
	)

	const processedLocationKey = useRef(null)

	useEffect(() => {
		if (targetSelectedUserId) {
			if (processedLocationKey.current === location.key) return
			const user = targetSelectedUserId
			processedLocationKey.current = location.key
			if (currentSelectedUser?._id !== user._id) {
				dispatch(
					setCurrentSelectedUser({
						...user,
						profilePic: user.avatar || user.profilePic,
					})
				)
				setChatType('user')
			}
		}
	}, [targetSelectedUserId, dispatch, currentSelectedUser?._id, location.key])

	const directChatList = useSelector((s) => s.message.directChatList)

	useEffect(() => {
		if (targetUserId && directChatList?.length > 0) {
			if (processedLocationKey.current === location.key) return
			const user = directChatList.find(u => u._id === targetUserId)
			if (user) {
				processedLocationKey.current = location.key
				if (currentSelectedUser?._id !== user._id) {
					dispatch(setCurrentSelectedGroup(null))
					dispatch(
						setCurrentSelectedUser({
							...user,
							profilePic: user.avatar || user.profilePic,
						})
					)
					setChatType('user')
				}
			}
		}
	}, [targetUserId, directChatList, dispatch, currentSelectedUser?._id, location.key])

	const groupList = useSelector((s) => s.group.groups)

	useEffect(() => {
		if (targetGroupId && groupList?.length > 0) {
			if (processedLocationKey.current === location.key) return
			const group = groupList.find(g => g._id === targetGroupId)
			if (group) {
				processedLocationKey.current = location.key
				if (currentSelectedGroup?._id !== group._id) {
					dispatch(setCurrentSelectedUser(null))
					dispatch(setCurrentSelectedGroup(group))
					setChatType('group')
				}
			}
		}
	}, [targetGroupId, groupList, dispatch, currentSelectedGroup?._id, location.key])

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (kazooId) {
				dispatch(getDirectChatUsers({ chatType: 'inSide', kazooId, search: searchQuery }))
			} else {
				dispatch(getDirectChatUsers({ chatType: 'outSide', search: searchQuery }))
			}
			dispatch(getUserGroups({ kazooId, search: searchQuery }))
		}, 500)

		return () => clearTimeout(delayDebounceFn)
	}, [dispatch, kazooId, searchQuery])

	useEffect(() => {
		dispatch(getUserGroups({ kazooId }))
		dispatch(getFriendsInfo())
	}, [dispatch, kazooId])

	// Global socket listener for live message updates in chat list
	const socket = useSelector((state) => state.auth.socket)

	useEffect(() => {
		if (!socket) return

		const handleNewMessage = (data) => {
			const message = data?.message || data
			const unreadCount = data?.unreadCount
			const senderData = data?.senderData

			// Always update the chat list with the latest message
			dispatch(updateChatListMessage({ message, currentUserId: me._id, senderData }))

			// If message is from a different user (not current chat), update unread count
			if (message?.senderId !== currentSelectedUser?._id && unreadCount !== undefined) {
				dispatch(updateUnreadCount({
					userId: message.senderId,
					count: unreadCount
				}))
			}

			// 🎯 Show indicator if user is viewing group chats
			if (chatType === 'group') {
				setHasNewDirectMessages(true)
			}
		}

		const handleNewGroupMessage = (data) => {
			const message = data?.message || data
			const unreadCount = data?.unreadCount

			// Always update the group list with the latest message
			dispatch(updateGroupListMessage({ message, currentUserId: me._id }))

			// If message is from a different group (not current chat), update unread count
			if (message?.groupId !== currentSelectedGroup?._id && unreadCount !== undefined) {
				dispatch(updateGroupUnreadCount({
					groupId: message.groupId,
					count: unreadCount
				}))
			}

			// 🎯 Show indicator if user is viewing direct chats
			if (chatType === 'user') {
				setHasNewGroupMessages(true)
			}
		}

		const handleNewGroupCreated = (data) => {
			console.log('🎯 newGroupCreated event received:', data)
			console.log('🎯 Current user ID:', me._id)
			console.log('🎯 Creator ID:', data.creatorId)

			const { group, creatorId } = data

			// Only add the group if current user is NOT the creator
			// (creator already has it added via createGroup.fulfilled)
			if (String(creatorId) !== String(me._id)) {
				console.log('✅ Adding group to list for non-creator')
				dispatch(addNewGroup({ group }))

				// If user is viewing direct chats, show indicator
				if (chatType === 'user') {
					setHasNewGroupMessages(true)
				}
			} else {
				console.log('⏭️ Skipping - current user is the creator')
			}
		}

		socket.on('newMessage', handleNewMessage)
		socket.on('newGroupMessage', handleNewGroupMessage)
		socket.on('newGroupCreated', handleNewGroupCreated)

		return () => {
			socket.off('newMessage', handleNewMessage)
			socket.off('newGroupMessage', handleNewGroupMessage)
			socket.off('newGroupCreated', handleNewGroupCreated)
		}
	}, [socket, dispatch, currentSelectedUser, currentSelectedGroup, me._id, chatType])

	return (
		<section className={styles.chatPage}>
			<div className={styles.pageHeader}>
				<div className={styles.headerLeft}>
					<h1>Chat</h1>
				</div>
				<div className={styles.pageHeaderRight}>
					<DropdownButton
						data-cy="create-groupchat"
						pluseIcon={true}
						label="Chat"
						options={chatDropdown}
						variant="default"
						size="lg"
						onSelect={handleSelect}
					/>
				</div>
			</div>
			<div
				className={`${styles.chatWrapper} ${isSidebarOpen ? styles.sidebarOpen : ''}`}
			>
				{/* Mobile Overlay */}
				<div
					className={`${styles.mobileOverlay} ${chatOption === 'view_group' ? styles.active : ''}`}
					onClick={handleCloseLeave}
				/>

				{/* Sidebar */}
				<aside className={`${styles.chatSidebar}`}>
					<div className={styles.sidebarTabs}>
						<div className={styles.tabs}>
							<ButtonCustom
								variant="outlined"
								size="lg"
								className={
									chatType === 'user' ? styles.active : ''
								}
								onClick={() => handleSetChatType('user')}
								data-cy="directChats"
								style={{ position: 'relative' }}
							>
								<img src="/images/user.svg" alt="user" /> Direct
								{hasNewDirectMessages && chatType !== 'user' && (
									<span className={styles.tabIndicator}></span>
								)}
							</ButtonCustom>
							<ButtonCustom
								variant="outlined"
								size="lg"
								className={
									chatType === 'group' ? styles.active : ''
								}
								onClick={() => handleSetChatType('group')}
								data-cy="groupChats"
								style={{ position: 'relative' }}
							>
								<img src="/images/people.svg" alt="group" />
								Group
								{hasNewGroupMessages && chatType !== 'group' && (
									<span className={styles.tabIndicator}></span>
								)}
							</ButtonCustom>
						</div>
					</div>

					<div className={styles.searchContainer}>
						<InputCustom
							value={searchQuery}
							type="text"
							name="search"
							id="search"
							placeholder={`Search ${chatType === 'user' ? 'Members' : 'Groups'}`}
							error={false}
							searchIcon={true}
							onChange={handleSearchQueryChange}
						/>
					</div>

					{chatType === 'user' ? (
						<DirectChatList
							closeSidebar={closeSidebar}
							toggleSidebar={toggleSidebar}
							statusMap={statusMap}
							searchQuery={searchQuery}
						/>
					) : (
						<GroupChatList
							closeSidebar={closeSidebar}
							toggleSidebar={toggleSidebar}
							statusMap={statusMap}
							searchQuery={searchQuery}
						/>
					)}
				</aside>

				{/* Chat Window */}
				<main
					className={`${styles.chatWindow} ${isSidebarOpen ? styles.open : ''}`}
				>
					{chatType === 'user' ? (
						<DirectChatWindow
							handleOptionSelect={handleOptionSelect}
							closeSidebar={closeSidebar}
							statusMap={statusMap}
							me={me}
						/>
					) : (
						<GroupChatWindow
							handleOptionSelect={handleOptionSelect}
							closeSidebar={closeSidebar}
							statusMap={statusMap}
							me={me}
						/>
					)}
				</main>

				{/* Group Info */}
				<GroupInfo
					handleClose={handleCloseLeave}
					setNewChatType={setNewChatType}
					chatOption={chatOption}
					ChatMembersModalOpen={ChatMembersModalOpen}
					statusMap={statusMap}
					setChatOption={setChatOption}
				/>
			</div>
			<ChatMembersModal
				open={chatMembers}
				handleClose={ChatMembersModalClose}
				setNewChatType={setNewChatType}
				statusMap={statusMap}
			/>
			{newChatType === 'individual' && (
				<IndividualModal
					open={newChatType === 'individual'}
					handleClose={handleCloseModal}
					onSuccess={handleSuccessIndividualModal}
					statusMap={statusMap}
				/>
			)}
			{newChatType === 'group' && (
				<GroupModal
					isNewGroup={chatOption !== 'view_group'}
					handleClose={handleCloseModal}
				/>
			)}
			{showUpgradePopup && (
				<GeneralPaymentPopup clickHandler={() => setShowUpgradePopup(false)} />
			)}

			<LeaveConfirmationModal
				open={chatOption === 'leave'}
				onClose={handleCloseLeave}
				onLeave={handleLeave}
			/>

			<CommonDeleteModal
				open={showDeleteModal}
				handleClose={handleCloseDeleteModal}
				heading="Delete Conversation"
				subHeading="Are you sure you want to Delete Conversation?"
				footerContent={
					<>
						<ButtonCustom
							variant="outlined"
							size="lg"
							onClick={handleCloseDeleteModal}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="destructive"
							size="lg"
							onClick={handleConfirmDelete}
							style={{ backgroundColor: '#D32F2F', color: '#fff' }}
						>
							Delete
						</ButtonCustom>
					</>
				}
			/>

			<CommonDeleteModal
				open={showMuteModal}
				handleClose={handleCloseMuteModal}
				heading={
					(chatType === 'user' ? currentSelectedUser?.isMuted : currentSelectedGroup?.isMuted)
						? 'Unmute Notifications'
						: 'Mute Notifications'
				}
				subHeading={`Are you sure you want to ${(chatType === 'user' ? currentSelectedUser?.isMuted : currentSelectedGroup?.isMuted) ? 'unmute' : 'mute'
					} notifications for this conversation?`}
				Icon={(chatType === 'user' ? currentSelectedUser?.isMuted : currentSelectedGroup?.isMuted) ? '/images/Mute-Notification.svg' : '/images/bell.svg'}
				footerContent={
					<>
						<ButtonCustom
							variant="outlined"
							size="lg"
							onClick={handleCloseMuteModal}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="destructive"
							size="lg"
							onClick={handleConfirmMute}
							style={{
								backgroundColor: (chatType === 'user' ? currentSelectedUser?.isMuted : currentSelectedGroup?.isMuted)
									? '#10B981'
									: '#F59E0B',
								color: '#fff',
							}}
						>
							{(chatType === 'user' ? currentSelectedUser?.isMuted : currentSelectedGroup?.isMuted) ? 'Unmute' : 'Mute'}
						</ButtonCustom>
					</>
				}
			/>
		</section>
	)
}
